import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiTarget, FiMessageSquare, FiFileText, FiTrendingUp } from 'react-icons/fi';
import { api } from '../utils/api';
import { useRefreshListener, REFRESH_EVENTS } from '../utils/refreshEvents';
import './Services.css';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Tous les services', icon: FiTarget },
    { id: 'strategie', name: 'Stratégie & Coaching', icon: FiTarget },
    { id: 'communication', name: 'Communication & Création', icon: FiMessageSquare },
    { id: 'administratif', name: 'Assistance Administrative', icon: FiFileText },
    { id: 'soutien', name: 'Soutien Entreprises', icon: FiTrendingUp }
  ];

  const fetchServices = useCallback(async () => {
    try {
      console.log('🔍 Chargement des services...');
      console.log('📡 API_URL:', process.env.REACT_APP_API_URL || 'Non défini (utilise proxy)');
      const res = await api.getServices();
      console.log('✅ Réponse API:', res);
      console.log('📦 Données reçues:', res.data);
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        setServices(res.data);
        console.log(`✅ ${res.data.length} service(s) chargé(s)`);
      } else {
        console.warn('⚠️ Aucun service trouvé, utilisation des services par défaut');
        setServices(getDefaultServices());
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des services:', error);
      console.error('📋 Détails de l\'erreur:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      console.log('🔄 Utilisation des services par défaut');
      setServices(getDefaultServices());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Écouter les événements de rafraîchissement depuis l'admin
  useRefreshListener(REFRESH_EVENTS.SERVICES, fetchServices);

  const getDefaultServices = () => [
    {
      _id: '1',
      title: 'Stratégie & Coaching Entrepreneurial',
      description: 'Clarifier la vision et les objectifs, construire un plan d\'action adapté à la personnalité de l\'entrepreneur.',
      category: 'strategie',
      image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=600&fit=crop',
      features: [
        'Clarifier la vision et les objectifs',
        'Construire un plan d\'action adapté à la personnalité de l\'entrepreneur',
        'Coaching de motivation et de discipline (lutter contre la procrastination, suivi des engagements)',
        'Développement de la confiance et mise en avant des points forts'
      ]
    },
    {
      _id: '2',
      title: 'Communication & Création',
      description: 'Création de supports visuels, rédaction de contenus, podcasts et stratégies de communication.',
      category: 'communication',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
      features: [
        'Création de supports (Canva, présentations, documents visuels)',
        'Rédaction de contenus (posts, newsletters, mails)',
        'Podcasts & voix-off publicitaire',
        'Élaboration et suivi de stratégies de communication'
      ]
    },
    {
      _id: '3',
      title: 'Assistance Administrative & Organisationnelle',
      description: 'Gestion de mails, veille réglementaire, classement, archivage et délégation de tâches chronophages.',
      category: 'administratif',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop',
      features: [
        'Gestion de mails, courriers, réponses administratives',
        'Veille réglementaire et administrative',
        'Classement, archivage, suivi administratif',
        'Délégation des tâches chronophages',
        'Gestion d\'agenda et priorisation'
      ]
    },
    {
      _id: '4',
      title: 'Soutien pour les entreprises (en phase de croissance)',
      description: 'Délégation administrative, prospection, relation client et assistance comptable pour les entreprises en croissance.',
      category: 'soutien',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop',
      features: [
        'Délégation administrative: gestion de dossiers, suivi clients',
        'Prospection & Relation Client: recherche de nouveaux prospects, relance, service après vente',
        'Assistance comptable: saisie de factures, suivi de paiements, organisation des justificatifs comptable'
      ]
    }
  ];

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(s => s.category === selectedCategory);

  const getCategoryIcon = (category) => {
    const icons = {
      strategie: '🎯',
      communication: '📢',
      administratif: '📋',
      soutien: '🚀'
    };
    return icons[category] || '📋';
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="services-page">
      <div className="page-header">
        <div className="page-header-background"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Nos Services</h1>
            <p>Découvrez nos 4 pôles de services pour vous accompagner dans votre réussite</p>
          </motion.div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <motion.div
            className="category-filters"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <motion.button
                  key={cat.id}
                  className={`filter-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon className="filter-icon" />
                  {cat.name}
                </motion.button>
              );
            })}
          </motion.div>

          {loading ? (
            <div className="loading">Chargement des services...</div>
          ) : (
            <motion.div
              className="services-list"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {filteredServices.map((service, index) => (
                <motion.div
                  key={service._id}
                  className="service-detail-card"
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="service-detail-image">
                    <img
                      src={service.image || `https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop&sig=${index}`}
                      alt={service.title}
                      loading="lazy"
                    />
                    <div className="service-image-overlay"></div>
                  </div>
                  <div className="service-detail-content">
                    <div className="service-detail-header">
                      <div className="service-detail-icon">
                        {getCategoryIcon(service.category)}
                      </div>
                      <h2>{service.title}</h2>
                    </div>
                    <p className="service-detail-description">{service.description}</p>
                    {service.features && service.features.length > 0 && (
                      <div className="service-features-list">
                        <h3>Nos prestations :</h3>
                        <ul>
                          {service.features.map((feature, i) => (
                            <li key={i}>
                              <FiCheckCircle className="feature-check-icon" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Services;
